// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { linkIconStyleGenerator, pageTitlesPrintStyle } from "@bg/App/SharedStyles/GeneralStyles";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqConfirmation.quoteId),
    rfqMyQuotesPageUrl: getPageLinkByPageType(state, "RfqMyQuotesPage")?.url,
    homePageUrl: getHomePageUrl(state),
});

type Props = WidgetProps & HasHistory & ReturnType<typeof mapStateToProps>;

export interface RfqConfirmationHeaderStyles {
    headerGridContainer?: InjectableCss;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: InjectableCss;
    viewQuotesButton?: ButtonPresentationProps;
    continueButton?: ButtonPresentationProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: InjectableCss;
    printClickable?: LinkPresentationProps;
    viewQuotesClickable?: ClickableProps;
    continueClickable?: ClickableProps;
    requestedBadge?: TypographyPresentationProps;
    badgeWrapper?: InjectableCss;
}

export const rfqConfirmationHeaderStyles: RfqConfirmationHeaderStyles = {
    headerGridContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 50px;
            @media (max-width: 450px) {
                flex-wrap: wrap;
            }
        `,
    },
    badgeWrapper: {
        css: css`
            flex-grow: 1;
            display: flex;
            align-items: flex-end;
            height: 40px;
        `,
    },
    viewQuotesButton: {
        css: css``,
    },
    continueButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            display: flex;
            justify-content: flex-end;
        `,
    },
    titleGridItem: {
        css: css``,
    },
    title: {
        variant: "h1",
        css: css`
            ${pageTitlesPrintStyle()}
        `,
    },
    printClickable: linkIconStyleGenerator({
        icon: "Print",
        textSize: "13px",
        iconSize: 14,
    }),
    buttonsHiddenContainer: {
        css: css`
            display: none;
            @media (min-width: 1025px) {
                display: block;
            }
        `,
    },
    menuHiddenContainer: {
        css: css`
            display: none;
            @media (max-width: 1024px) {
                display: block;
            }
        `,
    },
    requestedBadge: {
        size: "13px",
        lineHeight: "20px",
        color: "info",
        css: css`
            border: 1px solid ${getColor("info.main")};
            padding: 0 8px;
            border-radius: 10px;
            display: inline-block;
            margin-left: 15px;
            text-transform: uppercase;
            @media (max-width: 650px) {
                margin: 0;
            }
        `,
    },
};

const styles = rfqConfirmationHeaderStyles;

const RfqConfirmationHeader: FC<Props> = ({ quoteState, history, rfqMyQuotesPageUrl, homePageUrl }) => {
    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch({
            type: "commonActionSetter/SetAction",
            payload: {
                name: "breadcrumbActions",
                value: (
                    <StyledWrapper
                        css={css`
                            @media (max-width: 1024px) {
                                display: none;
                            }
                        `}
                    >
                        <Link {...styles.printClickable} onClick={openPrintDialog}>
                            {translate("Print")}
                        </Link>
                    </StyledWrapper>
                ),
            },
        });
        return () => {
            dispatch({
                type: "commonActionSetter/SetAction",
                payload: {
                    name: "breadcrumbActions",
                    value: null,
                },
            });
        };
    }, []);

    const viewQuotesClickHandler = () => {
        if (rfqMyQuotesPageUrl) {
            history.push(rfqMyQuotesPageUrl);
        }
    };

    const continueClickHandler = () => {
        history.push(homePageUrl);
    };

    const viewQuotesLabel = translate("View My Quotes");
    const continueLabel = translate("Continue Shopping");

    if (!quoteState.value) {
        return null;
    }

    const quote = quoteState.value;
    return (
        <StyledWrapper {...styles.headerGridContainer}>
            <StyledWrapper {...styles.titleGridItem}>
                <Typography {...styles.title}>
                    {translate("Quote#")}&nbsp;
                    <span data-test-selector="quoteNumber">{quote.quoteNumber}</span>
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.badgeWrapper}>
                <Typography {...styles.requestedBadge}>{translate("Requested")}</Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonGridItem}>
                <Hidden {...styles.menuHiddenContainer}>
                    <OverflowMenu {...styles.narrowOverflowMenu}>
                        <Clickable onClick={openPrintDialog}>{translate("Print")}</Clickable>
                        <Clickable {...styles.viewQuotesClickable} onClick={viewQuotesClickHandler}>
                            {viewQuotesLabel}
                        </Clickable>
                        <Clickable {...styles.continueClickable} onClick={continueClickHandler}>
                            {continueLabel}
                        </Clickable>
                    </OverflowMenu>
                </Hidden>
                <Hidden {...styles.buttonsHiddenContainer}>
                    <Button {...styles.viewQuotesButton} onClick={viewQuotesClickHandler}>
                        {viewQuotesLabel}
                    </Button>
                    <Button variant="secondary" {...styles.continueButton} onClick={continueClickHandler}>
                        {continueLabel}
                    </Button>
                </Hidden>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(RfqConfirmationHeader)),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RfqConfirmationPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Confirmation",
    },
};

export default widgetModule;
